/**
 * API client.
 *
 * One place that knows about transport concerns: the optional API key, how the
 * backend reports errors, and how a download is triggered from a blob. Views
 * deal in typed data only.
 */

import type {
  AccountProfile,
  AppConfig,
  AuthResult,
  HistoryPage,
  IdsDocumentSummary,
  IdsInspection,
  IdsLockSummary,
  JobResult,
  JobStatus,
  MeResult,
  OrgInfo,
  OrgPricing,
  OrgSeat,
  OrgSummary,
  PendingPurchaseRequest,
  SeatPurchaseRequest,
  SimpleRuleDraft,
  TemplateSummary,
} from './types';

const API_KEY_STORAGE = 'clearifc.apiKey';
// Deliberately a separate key from API_KEY_STORAGE, and never sent as
// X-API-Key -- CLEARIFC_ADMIN_KEY is a platform-wide secret (gates the
// account/org administration API for every customer, not one account's own
// data), so it's stored and transmitted completely independently, only via
// platformAdminRequest below. See MASTER_GUIDE.md and the /platform-admin
// route in App.tsx.
const ADMIN_KEY_STORAGE = 'clearifc.platformAdminKey';

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }

  /** True when the failure is "your plan does not include this". */
  get isPaymentRequired(): boolean {
    return this.status === 402;
  }

  /** True for a 502/503/504 -- the shape of failure you get while the
   * server is still cold-starting after being idle (see fly.toml's
   * min_machines_running = 0), not a real application error. Fly's own
   * proxy returns these before the app is even listening, so there's no
   * JSON body to read a useful message from -- this is what lets the UI
   * tell "still booting, try again" apart from a genuine failure. */
  get isServiceWarmingUp(): boolean {
    return this.status === 502 || this.status === 503 || this.status === 504;
  }
}

export function getApiKey(): string {
  return localStorage.getItem(API_KEY_STORAGE) ?? '';
}

export function setApiKey(key: string): void {
  if (key.trim()) localStorage.setItem(API_KEY_STORAGE, key.trim());
  else localStorage.removeItem(API_KEY_STORAGE);
}

function authHeaders(): Record<string, string> {
  const key = getApiKey();
  return key ? { 'X-API-Key': key } : {};
}

async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.detail === 'string') return body.detail;
    if (Array.isArray(body?.detail)) return body.detail.map((d: never) => String(d)).join('; ');
    return JSON.stringify(body);
  } catch {
    return response.statusText || `Request failed with status ${response.status}`;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    // Sends the login-session cookie (routes_auth.py) alongside the app's
    // own origin. 'same-origin' rather than 'include': this app never calls
    // a cross-origin API, so there is no reason to leak the cookie to one.
    credentials: 'same-origin',
    headers: { ...authHeaders(), ...(init.headers ?? {}) },
  });
  if (!response.ok) throw new ApiError(await readError(response), response.status);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function getPlatformAdminKey(): string {
  return localStorage.getItem(ADMIN_KEY_STORAGE) ?? '';
}

export function setPlatformAdminKey(key: string): void {
  if (key.trim()) localStorage.setItem(ADMIN_KEY_STORAGE, key.trim());
  else localStorage.removeItem(ADMIN_KEY_STORAGE);
}

/** Same shape as request(), but authenticates with X-Admin-Key instead of
 * X-API-Key -- every platform-admin-only org endpoint (create org, list
 * all orgs, set pricing, add an org's first admin, fulfil a purchase
 * request) goes through this, never through the normal request(). */
async function platformAdminRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const key = getPlatformAdminKey();
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: { 'X-Admin-Key': key, ...(init.headers ?? {}) },
  });
  if (!response.ok) throw new ApiError(await readError(response), response.status);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function requestBlob(path: string): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: authHeaders(),
  });
  if (!response.ok) throw new ApiError(await readError(response), response.status);
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return { blob: await response.blob(), filename: match?.[1] ?? 'clearifc-report' };
}

/** Hand a generated file to the browser as a download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next tick so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ------------------------------------------------------------------------ meta
export const getConfig = () => request<AppConfig>('/config');

// ------------------------------------------------------------------------ auth
function jsonPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export const signup = (payload: { email: string; password: string; name?: string }) =>
  jsonPost<AuthResult>('/auth/signup', payload);

export const login = (payload: { email: string; password: string }) =>
  jsonPost<AuthResult>('/auth/login', payload);

export const logout = () => jsonPost<{ logged_out: boolean }>('/auth/logout', {});

export const getMe = () => request<MeResult>('/auth/me');

export const forgotPassword = (email: string) =>
  jsonPost<{ message: string }>('/auth/forgot-password', { email });

export const resetPassword = (token: string, new_password: string) =>
  jsonPost<{ reset: boolean }>('/auth/reset-password', { token, new_password });

export const verifyEmail = (token: string) =>
  jsonPost<{ verified: boolean }>('/auth/verify-email', { token });

export const resendVerification = () =>
  jsonPost<{ message: string }>('/auth/resend-verification', {});

// --------------------------------------------------------------------- account
export const getAccount = () => request<AccountProfile>('/account');

export const rotateApiKey = () =>
  jsonPost<AccountProfile & { api_key: string; warning: string }>('/account/rotate-key', {});

// ---------------------------------------------------------------------- billing
export interface CheckoutResult {
  checkout_url: string;
  account_id: string;
}

/** Starts a Professional-tier subscription for the logged-in/API-key account. */
export const startCheckout = (payload: { period?: 'monthly' | 'annual' }) =>
  request<CheckoutResult>('/billing/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

/** Stripe's hosted portal for an existing subscriber to manage/cancel or update their card. */
export const openBillingPortal = () =>
  request<{ portal_url: string }>('/billing/portal', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });

// ------------------------------------------------------------------ templates
export const listTemplates = () =>
  request<{ templates: TemplateSummary[]; can_save: boolean }>('/templates');

export const getTemplate = (id: string) => request<Record<string, unknown>>(`/templates/${id}`);

export const saveTemplate = (payload: {
  name: string;
  description?: string;
  sector?: string;
  based_on?: string | null;
  definition: Record<string, unknown>;
}) =>
  request<TemplateSummary>('/templates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

export const deleteTemplate = (id: string) =>
  request<void>(`/templates/${id}`, { method: 'DELETE' });

// ----------------------------------------------------------------- validation
export interface ValidateOptions {
  file: File;
  templateId?: string;
  idsFile?: File | null;
  idsId?: string | null;
}

export async function startValidation(options: ValidateOptions): Promise<JobStatus> {
  const form = new FormData();
  form.append('file', options.file);
  if (options.templateId) form.append('template_id', options.templateId);
  if (options.idsFile) form.append('ids_file', options.idsFile);
  if (options.idsId) form.append('ids_id', options.idsId);
  return request<JobStatus>('/validate', { method: 'POST', body: form });
}

export async function startBatch(options: {
  files: File[];
  templateId?: string;
  idsFile?: File | null;
  idsId?: string | null;
  name?: string;
}): Promise<JobStatus> {
  const form = new FormData();
  options.files.forEach((file) => form.append('files', file));
  if (options.templateId) form.append('template_id', options.templateId);
  if (options.idsFile) form.append('ids_file', options.idsFile);
  if (options.idsId) form.append('ids_id', options.idsId);
  if (options.name) form.append('name', options.name);
  return request<JobStatus>('/batch', { method: 'POST', body: form });
}

export const getJob = (jobId: string) => request<JobStatus>(`/jobs/${jobId}`);
export const getJobResult = (jobId: string) => request<JobResult>(`/jobs/${jobId}/result`);

/** Poll a job to completion, reporting progress as it goes. */
export async function pollJob(
  jobId: string,
  onProgress: (status: JobStatus) => void,
  intervalMs = 600,
): Promise<JobResult> {
  for (;;) {
    const status = await getJob(jobId);
    onProgress(status);
    if (status.state === 'failed') {
      throw new ApiError(status.error ?? 'Validation failed.', 500);
    }
    if (status.state === 'complete') return getJobResult(jobId);
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

export type ReportFormat = 'pdf' | 'csv' | 'bcf';

export async function downloadJobReport(
  jobId: string,
  format: ReportFormat,
  index?: number,
): Promise<void> {
  const query = index === undefined ? '' : `?index=${index}`;
  const { blob, filename } = await requestBlob(`/jobs/${jobId}/report.${format}${query}`);
  saveBlob(blob, filename);
}

export async function downloadRunReport(runId: string, format: ReportFormat): Promise<void> {
  const { blob, filename } = await requestBlob(`/runs/${runId}/report.${format}`);
  saveBlob(blob, filename);
}

export interface EmailReportPayload {
  recipientEmail: string;
  message?: string;
  includeBcf?: boolean;
}

function _emailReportBody(payload: EmailReportPayload) {
  return {
    recipient_email: payload.recipientEmail,
    message: payload.message ?? '',
    include_bcf: payload.includeBcf ?? false,
  };
}

/** Emails the report (PDF, plus a BCF issue set when requested) straight
 * to a client or designer -- see routes_validate.py's email_job_report. */
export const emailJobReport = (jobId: string, payload: EmailReportPayload, index?: number) =>
  request<{ sent: boolean; recipient_email: string }>(
    `/jobs/${jobId}/email-report${index === undefined ? '' : `?index=${index}`}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(_emailReportBody(payload)),
    },
  );

export const emailRunReport = (runId: string, payload: EmailReportPayload) =>
  request<{ sent: boolean; recipient_email: string }>(`/runs/${runId}/email-report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(_emailReportBody(payload)),
  });

// -------------------------------------------------------------------- history
export const getHistory = (limit = 25, offset = 0) =>
  request<HistoryPage>(`/history?limit=${limit}&offset=${offset}`);

export const getRun = (runId: string) =>
  request<{ report: import('./types').ValidationReport }>(`/runs/${runId}`);

/** Turns on (or re-fetches, if already on) this run's public read-only
 * link. Idempotent server-side -- calling it twice returns the same token. */
export const shareRun = (runId: string) =>
  request<{ share_token: string }>(`/runs/${runId}/share`, { method: 'POST' });

/** Revokes the link immediately -- anyone still holding the URL gets a 404
 * on their next request. */
export const unshareRun = (runId: string) =>
  request<{ shared: false }>(`/runs/${runId}/share`, { method: 'DELETE' });

/** The backend route behind this needs no login and no API key -- works
 * identically for a caller who isn't signed in at all and never will be. */
export const getSharedReport = (shareToken: string) =>
  request<{ filename: string; report: import('./types').ValidationReport }>(
    `/shared/${shareToken}`,
  );

// ------------------------------------------------------------------------ IDS
export async function inspectIds(file: File): Promise<IdsInspection> {
  const form = new FormData();
  form.append('file', file);
  return request<IdsInspection>('/ids/inspect', { method: 'POST', body: form });
}

export const listIdsDocuments = () =>
  request<{ documents: IdsDocumentSummary[] }>('/ids/documents');

export const deleteIdsDocument = (id: string) =>
  request<void>(`/ids/documents/${id}`, { method: 'DELETE' });

export const saveIdsDocument = (payload: {
  name: string;
  description?: string;
  xml: string;
  filename?: string;
}) =>
  request<IdsDocumentSummary>('/ids/documents', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

export interface BuildIdsPayload {
  info: {
    title: string;
    description?: string;
    author?: string;
    version?: string;
    purpose?: string;
    milestone?: string;
    ifc_version?: string;
  };
  rules: SimpleRuleDraft[];
  save_as?: string | null;
}

/** Build IDS XML from form rules; returns the XML plus any saved document id. */
export async function buildIds(
  payload: BuildIdsPayload,
): Promise<{ xml: string; filename: string; savedId: string | null }> {
  const response = await fetch('/api/ids/build', {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new ApiError(await readError(response), response.status);
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return {
    xml: await response.text(),
    filename: match?.[1] ?? 'clearifc-rules.ids',
    savedId: response.headers.get('X-ClearIFC-Ids-Id'),
  };
}

/** Imports a rule sheet (.xlsx) into a saved IDS document, going through the
 * same builder pipeline as the form-based rule builder (see
 * routes_ids.py's import_ids_from_excel). The result behaves exactly like
 * any other saved document afterwards -- lock it, download it, validate
 * against it. */
export async function importIdsFromExcel(
  file: File,
  name: string,
  ifcVersion = 'IFC2X3 IFC4',
): Promise<IdsDocumentSummary & { rule_count: number }> {
  const form = new FormData();
  form.append('file', file);
  form.append('name', name);
  form.append('ifc_version', ifcVersion);
  return request<IdsDocumentSummary & { rule_count: number }>('/ids/import-excel', {
    method: 'POST',
    body: form,
  });
}

export async function downloadIdsDocument(id: string): Promise<void> {
  const { blob, filename } = await requestBlob(`/ids/documents/${id}/download`);
  saveBlob(blob, filename);
}

export interface LockRequestInput {
  requestedByName?: string;
  requestedByEmail?: string;
  projectName?: string;
  comments?: string;
}

/** Locks a saved IDS into a signed, tamper-evident .idsx and downloads it
 * (Enterprise -- see app/ids/lock.py). Returns the lock id from the response
 * header so the caller can show it immediately, without a second round trip
 * to GET /ids/locks. Every field on `request` is optional provenance --
 * signed into the envelope, shown on the report and the master-portal
 * decryptor, never required to lock. */
export async function lockIdsDocument(
  id: string,
  request?: LockRequestInput,
): Promise<{ lockId: string; filename: string }> {
  const response = await fetch(`/api/ids/documents/${id}/lock`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      requested_by_name: request?.requestedByName ?? '',
      requested_by_email: request?.requestedByEmail ?? '',
      project_name: request?.projectName ?? '',
      comments: request?.comments ?? '',
    }),
  });
  if (!response.ok) throw new ApiError(await readError(response), response.status);
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match?.[1] ?? 'locked.idsx';
  saveBlob(await response.blob(), filename);
  return { lockId: response.headers.get('X-ClearIFC-Lock-Id') ?? '', filename };
}

export const listIdsLocks = () => request<{ locks: IdsLockSummary[] }>('/ids/locks');

export const revokeIdsLock = (lockId: string) =>
  request<void>(`/ids/locks/${lockId}`, { method: 'DELETE' });

// -------------------------------------------------------------------------- org
export const getMyOrg = () => request<OrgInfo>('/org/me');

export const listOrgSeats = (search?: string) =>
  request<{ seats: OrgSeat[] }>(`/org/seats${search ? `?search=${encodeURIComponent(search)}` : ''}`);

export const addOrgSeat = (payload: { email: string; name?: string }) =>
  jsonPost<OrgSeat>('/org/seats', payload);

export const addOrgSeatsBulk = (emails: string[]) =>
  jsonPost<{ added: OrgSeat[]; failed: Array<{ email: string; detail: string }> }>(
    '/org/seats/bulk',
    { seats: emails.map((email) => ({ email })) },
  );

export const removeOrgSeat = (accountId: string) =>
  request<void>(`/org/seats/${accountId}`, { method: 'DELETE' });

export const resendOrgInvite = (accountId: string) =>
  jsonPost<{ sent: boolean }>(`/org/seats/${accountId}/resend-invite`, {});

export const deactivateOrgSeat = (accountId: string) =>
  jsonPost<OrgSeat>(`/org/seats/${accountId}/deactivate`, {});

export const reactivateOrgSeat = (accountId: string) =>
  jsonPost<OrgSeat>(`/org/seats/${accountId}/reactivate`, {});

export const setOrgSeatRole = (accountId: string, role: 'member' | 'admin') =>
  request<OrgSeat>(`/org/seats/${accountId}/role?role=${role}`, { method: 'POST' });

export const getOrgPricing = () => request<OrgPricing>('/org/pricing');

export const requestMoreSeats = (payload: {
  seats: number;
  payment_choice: 'pay_now' | 'invoice';
  start_date?: string;
}) => jsonPost<SeatPurchaseRequest>('/org/seats/purchase-requests', payload);

export const listPurchaseRequests = () =>
  request<{ requests: SeatPurchaseRequest[] }>('/org/seats/purchase-requests');

// --------------------------------------------------------- platform admin
// Everything below authenticates with the CLEARIFC_ADMIN_KEY (see
// getPlatformAdminKey/setPlatformAdminKey above), not a customer API key --
// this is the /platform-admin master portal's own surface.

function platformAdminJsonPost<T>(path: string, body: unknown): Promise<T> {
  return platformAdminRequest<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export const listAllOrgs = () => platformAdminRequest<{ orgs: OrgSummary[] }>('/org');

export const createOrgAsPlatformAdmin = (payload: {
  name: string;
  seat_limit: number;
  owner_account_id?: string;
}) => platformAdminJsonPost<OrgSummary>('/org', payload);

export const setOrgPricingAsPlatformAdmin = (
  orgId: string,
  payload: { price_per_seat_cents: number; term_start_date: string; term_end_date: string },
) =>
  platformAdminRequest<OrgSummary>(`/org/${orgId}/pricing`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

export const addOrgAdminAsPlatformAdmin = (orgId: string, payload: { email: string; name?: string }) =>
  platformAdminJsonPost<OrgSeat>(`/org/${orgId}/admins`, payload);

export const listPendingPurchaseRequests = () =>
  platformAdminRequest<{ requests: PendingPurchaseRequest[] }>('/org/purchase-requests/pending');

export const fulfilPurchaseRequestAsPlatformAdmin = (requestId: string) =>
  platformAdminJsonPost<{ org: OrgSummary; request: SeatPurchaseRequest }>(
    `/org/seats/purchase-requests/${requestId}/fulfil`,
    {},
  );

// Every account, every tier -- backs the master portal's "All accounts"
// section. Same /admin/accounts surface the admin API always had; nothing
// new on the backend, just a UI for what curl already could do.
export const listAllAccounts = () =>
  platformAdminRequest<{ accounts: AccountProfile[]; total: number }>('/admin/accounts');

export const updateAccountAsPlatformAdmin = (
  accountId: string,
  payload: { tier?: string; disabled?: boolean; notes?: string },
) =>
  platformAdminRequest<AccountProfile>(`/admin/accounts/${accountId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

export const rotateAccountKeyAsPlatformAdmin = (accountId: string) =>
  platformAdminJsonPost<AccountProfile & { api_key: string; warning: string }>(
    `/admin/accounts/${accountId}/rotate-key`,
    {},
  );

export interface DecryptedIdsx {
  lock_id: string;
  account_id: string;
  account_name: string;
  name: string;
  locked_at: string;
  ids_sha256: string;
  requested_by_name: string;
  requested_by_email: string;
  project_name: string;
  comments: string;
  xml: string;
}

/** Reads a locked .idsx file's actual contents -- platform-admin only, for
 * audits/disputes over what was locked (see routes_org.py's decrypt_idsx). */
export const decryptIdsxAsPlatformAdmin = async (file: File): Promise<DecryptedIdsx> => {
  const form = new FormData();
  form.append('file', file);
  return platformAdminRequest<DecryptedIdsx>('/org/idsx/decrypt', {
    method: 'POST',
    body: form,
  });
};
